import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ExternalLink } from 'lucide-react';

const NNG_HEURISTICS = [
  {
    id: 'h1_visibility',
    name: '1. Visibility of system status',
    summary:
      "The design should keep users informed about what's happening, through appropriate feedback within reasonable time.",
    look: [
      'Loading states for async work (spinners, skeletons, progress bars)',
      'Confirmation feedback after destructive or remote actions',
      'Current location indicators in nav (active route, breadcrumb)',
      'Toast/snackbar messaging for background results',
    ],
  },
  {
    id: 'h2_match',
    name: '2. Match between system and the real world',
    summary:
      "The design should speak the users' language with familiar words and concepts, following real-world conventions.",
    look: [
      "Domain-appropriate terminology rather than developer jargon",
      'Date / number / currency formatting that matches the user locale',
      'Iconography conventions (trash = delete, gear = settings)',
      'Workflow ordering that mirrors how the task is actually done',
    ],
  },
  {
    id: 'h3_user_control',
    name: '3. User control and freedom',
    summary:
      'Users frequently make mistakes. They need a clearly marked "emergency exit" to leave an unwanted state without going through an extended dialogue.',
    look: [
      'Undo / redo for non-trivial actions',
      'Cancel buttons on long-running operations',
      'Easy back-out from modals (esc key, X button, click outside)',
      'Browser back behavior matches user expectations',
    ],
  },
  {
    id: 'h4_consistency',
    name: '4. Consistency and standards',
    summary:
      'Users should not have to wonder whether different words, situations, or actions mean the same thing. Follow platform and industry conventions.',
    look: [
      'Reusable components instead of one-off variants',
      'Same visual treatment for the same kind of action everywhere',
      'Consistent terminology across pages (one of: "user / member / account")',
      'Standard form patterns for sign-in, search, settings',
    ],
  },
  {
    id: 'h5_error_prevention',
    name: '5. Error prevention',
    summary:
      'Good error messages matter; preventing the error in the first place matters more. Eliminate error-prone conditions or check for them before commit.',
    look: [
      'Disabled submit buttons when required fields are empty',
      'Confirmation dialogs for destructive actions',
      'Input masking and inline format validation',
      'Smart defaults that match the most common case',
    ],
  },
  {
    id: 'h6_recognition',
    name: '6. Recognition rather than recall',
    summary:
      "Minimize the user's memory load by making elements, actions, and options visible. The user shouldn't have to remember information from one part of the interface to another.",
    look: [
      'Auto-complete and recent-item lists',
      'Visible labels rather than icon-only buttons (or icon + label)',
      'Persistent breadcrumbs / steppers in multi-step flows',
      'Surfacing previously entered values as defaults on return',
    ],
  },
  {
    id: 'h7_flexibility',
    name: '7. Flexibility and efficiency of use',
    summary:
      'Shortcuts — unseen by novices — may speed up the interaction for the expert user. Allow users to tailor frequent actions.',
    look: [
      'Keyboard shortcuts for power-user actions',
      'Saved views, filters, or templates',
      'Bulk operations on table rows',
      'Customizable layout / dashboards',
    ],
  },
  {
    id: 'h8_aesthetic',
    name: '8. Aesthetic and minimalist design',
    summary:
      'Interfaces should not contain information that is irrelevant or rarely needed. Every extra unit of information competes with the relevant ones.',
    look: [
      'Restrained use of color, weight, and rule lines',
      'Information hierarchy via type scale, not boxes within boxes',
      'Progressive disclosure for advanced options',
      'No tooltips on things that should already be self-explanatory',
    ],
  },
  {
    id: 'h9_recover',
    name: '9. Help users recognize, diagnose, and recover from errors',
    summary:
      'Error messages should be expressed in plain language (no codes), precisely indicate the problem, and constructively suggest a solution.',
    look: [
      "Specific error text — not 'Something went wrong'",
      'Inline form errors next to the offending field',
      'Recovery actions in the error message itself ("Retry", "Edit and resubmit")',
      'Trace ids surfaced for support escalation',
    ],
  },
  {
    id: 'h10_help',
    name: '10. Help and documentation',
    summary:
      "Even better if the system can be used without documentation, but it may be necessary to provide help — focused on the user's task, listing concrete steps, and not too large.",
    look: [
      'Inline help (tooltips, "?" affordances) on jargon-heavy fields',
      'Empty-state copy that guides the first-run user',
      'Searchable help docs reachable in 1–2 clicks',
      'Examples and templates rather than abstract reference text',
    ],
  },
];

export function Learn() {
  return (
    <div className="mx-auto max-w-4xl px-6 py-12">
      <div className="mb-8">
        <h1 className="text-4xl font-semibold tracking-tight">Learn</h1>
        <p className="mt-2 text-[var(--color-muted-foreground)]">
          For UX designers and product folks: how Heuristic Hero evaluates your site, how to read
          the report, and which standards back each score. Underneath-the-hood implementation
          (the agent framework, OTel, infra) is on the <a href="/about" className="text-[var(--color-primary)] hover:underline">About</a> page.
        </p>
      </div>

      {/* The scoring formula in plain language */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>The score, in one paragraph</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            Heuristic Hero gives each evaluated page a single composite score 0–100, weighted across
            three dimensions:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li><strong>DESIGN.md alignment (50%)</strong> — Does the rendered page match the design contract you submitted? If you don't submit a DESIGN.md, this dimension is skipped and the other two re-weight to 60% / 40%.</li>
            <li><strong>NN/g heuristics (25%)</strong> — Each of Jakob Nielsen's 10 usability heuristics scored individually 0–100, with cited evidence (DOM elements or visual regions). The NN/g sub-score is the mean.</li>
            <li><strong>Accessibility (25%)</strong> — Real findings from the open-source <a href="https://github.com/dequelabs/axe-core" target="_blank" rel="noreferrer" className="text-[var(--color-primary)] hover:underline">axe-core</a> rules engine, prioritized by impact and translated into a 0–100 score. 100 = no violations.</li>
          </ul>
          <p>
            Take the score as a starting point for review, not as a final judgment. Manual review by
            a human designer always wins.
          </p>
        </CardContent>
      </Card>

      {/* NN/g 10 heuristics — the deep dive */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Nielsen Norman Group's 10 usability heuristics</CardTitle>
          <CardDescription>
            The canonical UX heuristics, first published by Jakob Nielsen in 1994 and refined since.
            Heuristic Hero scores all ten individually so you can see exactly which dimension a site
            stumbles on. Click each to expand.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {NNG_HEURISTICS.map((h) => (
              <details
                key={h.id}
                className="group rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/20 px-4 py-3 open:bg-[var(--color-muted)]/40"
              >
                <summary className="cursor-pointer list-none text-sm font-medium">
                  <span className="mr-2 text-[var(--color-muted-foreground)] group-open:rotate-90 inline-block transition-transform">▸</span>
                  {h.name}
                </summary>
                <div className="mt-3 space-y-2 pl-5 text-sm">
                  <p className="text-[var(--color-muted-foreground)]">{h.summary}</p>
                  <div>
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">What we look for</div>
                    <ul className="list-disc space-y-0.5 pl-5">
                      {h.look.map((l, i) => <li key={i}>{l}</li>)}
                    </ul>
                  </div>
                </div>
              </details>
            ))}
          </div>
          <p className="mt-4 text-xs text-[var(--color-muted-foreground)]">
            Source:{' '}
            <a
              href="https://www.nngroup.com/articles/ten-usability-heuristics/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              10 Usability Heuristics for User Interface Design (NN/g)
            </a>
          </p>
        </CardContent>
      </Card>

      {/* Reading the report */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Reading the report</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p><strong>Composite badge.</strong> The single 0–100 number at the top is the weighted average. Color tier — green ≥ 85, teal 70–84, amber 50–69, rose &lt; 50.</p>
          <p><strong>Sub-score badges.</strong> The three (or two) cards below the composite show each dimension's score and its weight. Click into the corresponding section card below for reasoning + pros/cons + cited evidence.</p>
          <p><strong>NN/g radar.</strong> Each axis is one of the ten heuristics. A circular shape means the page is balanced; a notched shape means there are specific weaknesses to address. Expand the table for per-heuristic evidence.</p>
          <p><strong>Accessibility bar chart.</strong> Counts of axe-core violations grouped by impact (critical / serious / moderate / minor). Critical and serious are usually blocking; moderate and minor are worth fixing but rarely launch-blockers.</p>
          <p><strong>Percentile callouts.</strong> Once we have ≥ 2 prior evaluations in the corpus, each score is shown as a percentile: "78th percentile composite" means this site beat 78% of prior submissions.</p>
          <p><strong>Domain history sparkline.</strong> If the same domain has been evaluated before, you'll see a small line chart of all its prior composite scores. Useful for tracking design changes across releases.</p>
          <p><strong>Cost.</strong> Every report discloses what the evaluation cost in cents. There's no hidden price — you're seeing the actual LLM tokens + container seconds rolled up.</p>
        </CardContent>
      </Card>

      {/* DESIGN.md */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>DESIGN.md — your design contract</CardTitle>
          <CardDescription>
            A Markdown file describing your design intent. Like a brief, but it lives in your repo
            next to the code it describes — so it can drift visibly when implementation diverges.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            Heuristic Hero takes your DESIGN.md, looks at the rendered page, and asks: <em>did the
            implementation honor the spec?</em> Mismatches show up as cons in the DESIGN.md
            alignment section: "primary CTA missing", "spacing scale not followed", "color tokens
            not applied".
          </p>
          <p>
            DESIGN.md works best when it's specific and falsifiable. Token values, named components,
            primary actions per page, the type scale — concrete things the agent can verify. Vague
            wishes ("modern feel", "clean") are hard to score.
          </p>
          <p>
            Don't have a DESIGN.md? You can still submit — we'll just skip the alignment dimension
            and score on NN/g + accessibility (60% / 40%).
          </p>
          <p>
            <a
              href="https://github.com/google-labs-code/design.md"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              The DESIGN.md spec on GitHub (google-labs-code/design.md)
            </a>
          </p>
        </CardContent>
      </Card>

      {/* Tooling for DESIGN.md authors */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Tooling for working with DESIGN.md</CardTitle>
          <CardDescription>Linters, editor integrations, and AI-coding rules that play nicely with the spec.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div>
            <h3 className="font-semibold">The official linter — <code className="font-mono">@google/design.md</code></h3>
            <p className="mt-1">
              Run as part of your CI or pre-commit so a malformed DESIGN.md never lands in main:
            </p>
            <pre className="mt-2 overflow-x-auto rounded bg-black/30 p-3 text-xs"><code>npm install --save-dev @google/design.md
npx @google/design.md lint DESIGN.md</code></pre>
            <p className="mt-2 text-[var(--color-muted-foreground)]">
              Heuristic Hero runs this exact command server-side on the DESIGN.md you submit and
              surfaces the results next to your alignment score. 0 errors / 0 warnings is the bar.
            </p>
          </div>

          <div>
            <h3 className="font-semibold">AI-coding agents — pin DESIGN.md as a constraint</h3>
            <p className="mt-1">
              When you ask a coding agent (Claude Code, Cursor, Copilot, etc.) to build UI, point it
              at your DESIGN.md so it generates components that already conform. Project-level files
              every modern agent reads:
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-6">
              <li><code className="font-mono">AGENTS.md</code> — multi-agent standard; reference DESIGN.md and add "always honor the tokens, never hardcode color/spacing."</li>
              <li><code className="font-mono">CLAUDE.md</code> / <code className="font-mono">.cursor/rules</code> — single-agent equivalents.</li>
              <li><code className="font-mono">.github/copilot-instructions.md</code> — Copilot project-level guidance.</li>
            </ul>
            <p className="mt-2 text-[var(--color-muted-foreground)]">
              A one-line addition like "When building UI, treat DESIGN.md as the source of truth for
              tokens, component contracts, and layout. Never invent new tokens." changes generated
              code measurably.
            </p>
          </div>

          <div>
            <h3 className="font-semibold">Claude Code Skills</h3>
            <p className="mt-1">
              <a href="https://www.anthropic.com/news/skills" target="_blank" rel="noreferrer" className="text-[var(--color-primary)] hover:underline">Anthropic Skills</a>{' '}
              are user-installable bundles that give Claude domain-specific capabilities. There's no
              official "DESIGN.md" Skill yet, but a useful one is a few lines of Markdown — describing
              when to apply (any DESIGN.md edit), where to find tokens (frontmatter), and which
              checks to run (<code className="font-mono">design.md lint</code>, then a local diff
              against the previous version). Drop it into your repo's
              <code className="font-mono"> .claude/skills/</code> directory and Claude Code picks it
              up automatically.
            </p>
          </div>

          <div>
            <h3 className="font-semibold">Editor integrations</h3>
            <ul className="mt-1 list-disc space-y-1 pl-6">
              <li>
                <strong>VS Code</strong> — any Markdown extension renders DESIGN.md fine; the
                YAML-Frontmatter-Highlighter and Prettier extensions help with the structured
                front-matter block specifically.
              </li>
              <li>
                <strong>Cursor</strong> — add a project rule: "When the user edits DESIGN.md,
                automatically run <code className="font-mono">npx @google/design.md lint DESIGN.md</code>{' '}
                in a terminal before declaring the edit complete." Cursor handles this through{' '}
                <code className="font-mono">.cursor/rules</code>.
              </li>
              <li>
                <strong>JetBrains IDEs</strong> — the built-in Markdown plugin renders frontmatter; the
                File Watchers feature can run the linter on save.
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold">CI integration</h3>
            <p className="mt-1">Heuristic Hero's own CI lints its DESIGN.md on every push. Shape:</p>
            <pre className="mt-2 overflow-x-auto rounded bg-black/30 p-3 text-xs"><code>{`# .github/workflows/ci.yml
- name: Lint DESIGN.md
  run: npx @google/design.md lint DESIGN.md`}</code></pre>
            <p className="mt-2 text-[var(--color-muted-foreground)]">
              Optional: pair with a status badge in your README showing "DESIGN.md ✓ lint passing".
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Accessibility */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Accessibility</CardTitle>
          <CardDescription>What axe-core finds, and what it doesn't.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            We run <strong>axe-core</strong> (the open-source rules engine maintained by Deque)
            inside a real headless Chromium. axe finds programmatic accessibility errors —
            missing alt text, low color contrast, missing landmarks, ARIA misuse, focus traps,
            and the like. Each violation is tagged with the specific WCAG success criterion it
            breaks.
          </p>
          <p>
            <strong>WCAG levels:</strong> the standard defines three conformance levels — A
            (essentials), AA (the de facto target for most public sites), and AAA (often
            unattainable across an entire site). Heuristic Hero reports against AA by default;
            our score reflects what axe scans, with the LLM weighting by impact.
          </p>
          <p>
            <strong>What automated testing misses</strong> — about 30–40% of real accessibility
            issues. axe is excellent at catching <em>things you can detect from the DOM</em>.
            It cannot tell you whether your alt text is meaningful, whether the keyboard order
            is logical, or whether a screen-reader user can actually accomplish their task. For
            those, no tool replaces a human review.
          </p>
          <p className="space-y-1">
            Authoritative sources:{' '}
            <a href="https://www.w3.org/TR/WCAG22/" target="_blank" rel="noreferrer" className="text-[var(--color-primary)] hover:underline">WCAG 2.2 (W3C)</a>{' · '}
            <a href="https://github.com/dequelabs/axe-core" target="_blank" rel="noreferrer" className="text-[var(--color-primary)] hover:underline">axe-core (Deque)</a>{' · '}
            <a href="https://www.w3.org/WAI/standards-guidelines/aria/" target="_blank" rel="noreferrer" className="text-[var(--color-primary)] hover:underline">WAI-ARIA</a>
          </p>
        </CardContent>
      </Card>

      {/* What we don't measure */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>What Heuristic Hero doesn't measure</CardTitle>
          <CardDescription>So you calibrate expectations.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1 text-sm">
            <li><strong>Performance.</strong> No Core Web Vitals, no Lighthouse score (yet). Use PageSpeed Insights for that.</li>
            <li><strong>SEO.</strong> No meta-tag analysis, no sitemap audit. Use the dedicated tools.</li>
            <li><strong>Content quality.</strong> We don't grade your prose, only how it's presented.</li>
            <li><strong>Brand fit.</strong> We score against your DESIGN.md, not against an imagined "good design" platonic ideal.</li>
            <li><strong>Security.</strong> Beyond accessibility, we don't probe for XSS, CSRF, or auth bugs.</li>
            <li><strong>Functional correctness.</strong> We render and observe; we don't click through flows or test that buttons do what they should.</li>
            <li><strong>Cross-browser issues.</strong> We render once in headless Chromium. Differences in Firefox/Safari/mobile are out of scope.</li>
          </ul>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card>
        <CardHeader>
          <CardTitle>Tips for getting a useful evaluation</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1 text-sm">
            <li><strong>Submit a representative page</strong>, not just the marketing splash. The home page is often the least typical page on a site.</li>
            <li><strong>Make your DESIGN.md falsifiable.</strong> "Primary CTA is teal" beats "buttons feel friendly".</li>
            <li><strong>Re-run after design changes.</strong> The corpus tracks per-domain history so you can see your scores trend.</li>
            <li><strong>Try a different model</strong> if the result feels off. gpt-5 catches more nuance; gpt-5-nano is much cheaper and good enough for repeated runs.</li>
            <li><strong>Read the cited evidence</strong>, not just the score. The pros/cons reference specific DOM nodes and screenshot regions.</li>
            <li><strong>Don't chase the score.</strong> A 92 isn't necessarily better-designed than an 84 with stronger evidence on the dimensions you care about.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
